import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { THRESHOLDS, TIMING } from '@erectus/shared';
import { Icon } from '@/ui/icons.js';

/**
 * What counts as good sitting here, in plain words, from the same constants
 * the classifier uses (scoring/frameState.ts). Answers the plumb-line
 * question: sitting is judged by three angles with tolerance bands, not by
 * how straight a line through ear, shoulder and hip is.
 */
export function SittingCriteria(): ReactNode {
  const t = THRESHOLDS;
  return (
    <details className="group rounded-2xl border border-line bg-white p-4">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 font-semibold [&::-webkit-details-marker]:hidden">
        How sitting posture is judged
        <Icon name="plus" size={18} className="shrink-0 text-muted transition group-open:rotate-45" />
      </summary>
      <div className="mt-3 space-y-3 text-sm text-ink-soft">
        <p>
          Angles from the side, a few times a second. A moment counts as good when none of them is out of range.
        </p>
        <ul className="space-y-2">
          <li>
            <strong className="text-ink">Neck angle (CVA).</strong> The line from the base of your neck (C7) to the
            front of your ear, against the horizontal (ear straight above = 90°). Below {t.cva.good}° counts as head
            forward. If you did the seated check, dropping more than {t.cva.personalDropBad}° below your own sit-tall
            angle counts too — whichever is stricter. The camera cannot see C7, so it is placed from your shoulder by
            average body proportions: read the number as an estimate.
          </li>
          <li>
            <strong className="text-ink">Head tilt (CHA).</strong> The line from the front of your ear to the outer
            corner of your eye, against the horizontal. More than {t.cha.max}° chin-up counts as head forward too.
            Looking down at a screen is not counted here.
          </li>
          <li>
            <strong className="text-ink">Trunk.</strong> The line from your hip to your shoulder, against vertical.{' '}
            {t.trunkInclination.goodMin}° to +{t.trunkInclination.goodMax}° — upright to a light recline — is good. More
            than {t.trunkInclination.warnMax}° forward counts as slouching; more than {Math.abs(t.trunkInclination.reclineBad)}° back
            as leaning back.
          </li>
          <li>
            <strong className="text-ink">Shoulders.</strong> How far your shoulder sits ahead of your hip, as a share of
            your torso length. More than {t.shoulderProtraction.warn} counts as slouching.
          </li>
        </ul>
        <p>
          <strong className="text-ink">Not a plumb line.</strong> Good sitting here does not mean ear, shoulder and hip
          stacked on one perfectly vertical line. Healthy people sit with the head a little ahead of the shoulders and
          often lean back slightly, so each angle has a band rather than a single “correct” value. The standing check
          works the same way: it compares you with measured healthy posture, not the textbook plumb line.
        </p>
        <p>
          A change has to last {TIMING.hysteresisSec} seconds before it shows, and {TIMING.episodeMinSec} seconds before it
          counts as an episode, so a quick glance down does not count against you.
        </p>
        <Link to="/sources" className="inline-block font-semibold text-ink underline">
          Where these numbers come from
        </Link>
      </div>
    </details>
  );
}
